import { Table } from 'ui/molecules/Table'
import { Pagination } from 'ui/molecules/Pagination'
import { useEffect, useMemo, useState } from 'react'
import { PAGINATION_DROPDOWN_LABEL } from 'texts/uiTexts'
import { HEAD_ITEMS } from 'texts/applications'
import { useNavigate } from 'react-router-dom'
import { ApplicationListProps } from './types'
import styles from './styles.module.scss'
import { TableHeadProps } from 'ui/molecules/Table/TableHead/types'
import { useApiRequest } from 'hooks/useApiRequest'
import { applicationsApi, ApplicationsApiTypes } from 'api'
import { useDispatch, useSelector } from 'react-redux'
import { Filter } from 'utils/addQueryString'
import { COR_MAP, FILTER_KEYS, REVERSE_COR_MAP } from './constants'
import { useFilterSettings } from 'hooks/useFilterSettings'
import {
  filterItemsSelector,
  selectedFiltersCountSelector,
  selectedFiltersSelector,
} from 'redux/filters/selectors'
import { setFilterItems } from 'redux/filters/slice'
import { sortingSelector } from 'redux/sorting/selectors'
import { APPLICATIONS_FILTER_KEY } from 'constants/filters'
import {
  APPLICATION_SEARCH_PARAM,
  APPLICATION_STATUS,
} from 'modules/Applications/constants'
import { ApplicationStatus } from 'api/applications/types'
import { Typography } from 'ui/atoms/Typography'
import { prepareFiltersForReq, prepareSortingForReq } from 'utils/table'
import { COR_LIST_WITHOUT_BLACK_LIST } from 'constants/countryOfResidence'
import { ALL } from 'texts/common'
import { TableTooltip } from 'ui/molecules/TableTooltip'
import { TableCellLink } from 'ui/atoms/TableCellLink'
import { ResetFiltersButton } from 'ui/components/ResetFiltersButton'
import { usePagination } from 'hooks/usePagination'

export const ApplicationList = ({
  status,
  selectedRange,
  isArchive,
}: ApplicationListProps) => {
  const selectedFiltersCount = useSelector(selectedFiltersCountSelector)
  const selectedFilters = useSelector(selectedFiltersSelector)
  const sortingData = useSelector(sortingSelector)
  const filterItems = useSelector(filterItemsSelector)
  const [applications, setApplications] = useState<
    ApplicationsApiTypes.ApplicationListResponse['applications'] | null
  >(null)

  const navigate = useNavigate()
  const dispatch = useDispatch()

  const { page, pageSize, handleResetPagination } = usePagination()

  const applicationListRequest = useApiRequest(
    (status, isArchive, fromDate, toDate, filters, sorting) =>
      applicationsApi.getApplicationList(
        page - 1,
        Number(pageSize),
        status,
        isArchive,
        fromDate,
        toDate,
        filters,
        sorting
      )
  )

  const applicationFiltersRequest = useApiRequest(
    (fromDate, toDate, isArchive) =>
      applicationsApi.getApplicationFilters(fromDate, toDate, isArchive)
  )

  const fetchApplicationFilters = () => {
    const fetchFilters = async () => {
      const applicationFiltersResponse =
        await applicationFiltersRequest.apiRequest(
          selectedRange?.from,
          selectedRange?.to,
          isArchive
        )

      if (applicationFiltersResponse) {
        const countries = applicationFiltersResponse.data.countries.map(
          (code) => COR_MAP.get(code)
        )
        const filterItems = Object.entries({
          ...applicationFiltersResponse.data,
          countries,
        }).reduce(
          (acc, [key, value]) => ({
            ...acc,
            [key]: value.map((el) => ({ value: el, selected: false })),
          }),
          {}
        )
        dispatch(
          setFilterItems({ items: filterItems, key: APPLICATIONS_FILTER_KEY })
        )
      }
    }
    fetchFilters()
  }

  useEffect(() => {
    !selectedFiltersCount.applications && fetchApplicationFilters()
  }, [status, selectedFiltersCount, selectedRange])

  useEffect(() => {
    const fetchFilteredCompanyList = async () => {
      let filters: Filter[] = []

      if (selectedFiltersCount) {
        filters = prepareFiltersForReq(
          {
            ...selectedFilters.applications,
            countries: selectedFilters.applications.countries?.map(
              (name) => REVERSE_COR_MAP.get(name) || ''
            ),
          },
          selectedFiltersCount.applications,
          'applications'
        )
      }

      const applicationListResponse = await applicationListRequest.apiRequest(
        status,
        isArchive,
        selectedRange.from,
        selectedRange.to,
        filters && filters,
        prepareSortingForReq(sortingData.applications, APPLICATIONS_FILTER_KEY)
      )
      if (applicationListResponse) {
        setApplications(applicationListResponse.data.applications)
      }
    }
    selectedRange?.from && selectedRange.to && fetchFilteredCompanyList()
  }, [
    page,
    pageSize,
    selectedFiltersCount,
    selectedRange,
    sortingData.applications,
    status,
  ])

  const getFilterSettings = useFilterSettings(
    APPLICATIONS_FILTER_KEY,
    handleResetPagination
  )

  const getApplicationPath = (id: string) =>
    `../${
      APPLICATION_SEARCH_PARAM[
        isArchive ? APPLICATION_STATUS.Archive : (status as ApplicationStatus)
      ]
    }/${id}`

  const handleRedirectToApplication = (id: string) => {
    navigate(getApplicationPath(id))
  }

  const formatCountries = (countries: string[]) => {
    if (countries.length === COR_LIST_WITHOUT_BLACK_LIST.length) {
      return ALL
    }

    return countries
      .map((code) => COR_MAP.get(code))
      .filter(Boolean)
      .join(', ')
  }

  const formattedBodyItems = useMemo(
    () =>
      applications?.pageItems.map((application) => {
        const items = {
          items: [
            <TableCellLink to={getApplicationPath(String(application.Id))}>
              {application.Id}
            </TableCellLink>,
            <TableCellLink to={getApplicationPath(String(application.Id))}>
              {application.companyName}
            </TableCellLink>,
            <TableTooltip
              title={formatCountries(application.countries)}
              onClick={() =>
                handleRedirectToApplication(String(application.Id))
              }
            />,
            application.submission,
            <Typography
              Tag="a"
              className={styles['email-link']}
              name="Subtitle7"
              color="blue700"
              target="_blank"
              onClick={(e) => {
                e.stopPropagation()
              }}
              href={`mailto:${application.companyEmail}`}
            >
              {application.companyEmail}
            </Typography>,
          ],
          onClick: () => handleRedirectToApplication(String(application.Id)),
        }

        if (status !== APPLICATION_STATUS.WaitingList) {
          items.items.push(
            application.status === APPLICATION_STATUS.WaitingList
              ? '-'
              : application.processTime
          )
          items.items.push(application.reviewedBy)
        }
        return items
      }),
    [applications, status]
  )

  const formattedHeadItems = useMemo(() => {
    const newHeadItems: TableHeadProps['item'][] = Object.values(
      HEAD_ITEMS
    ).map((header) => {
      if (Array.isArray(header)) {
        return {
          value: status === APPLICATION_STATUS.Reject ? header[1] : header[0],
        }
      } else {
        return {
          value: header,
        }
      }
    })
    if (status === APPLICATION_STATUS.WaitingList) {
      const ADDITIONAL_ITEMS_COUNT = 2
      newHeadItems.splice(
        newHeadItems.length - ADDITIONAL_ITEMS_COUNT,
        ADDITIONAL_ITEMS_COUNT
      )
    }

    const settingKey = Object.keys(FILTER_KEYS)
    newHeadItems.forEach(
      (headItem, i) =>
        (headItem.filterSettings = getFilterSettings(settingKey[i]))
    )
    return newHeadItems
  }, [filterItems, getFilterSettings, status])

  return (
    <div>
      <div className={styles.list}>
        <div className={styles.header}>
          <ResetFiltersButton
            selectedFiltersCount={selectedFiltersCount.applications}
          />
        </div>
        <div className={styles.table}>
          <Table
            headItems={formattedHeadItems}
            bodyItems={formattedBodyItems}
            className={styles.thead}
            initItemsWidth={['60', '200', '200', '171', '200', '100']}
          />
        </div>
      </div>
      <footer className={styles.pagination}>
        <Pagination
          defaultPage={page}
          defaultPageSize={Number(pageSize)}
          pageItemCount={applications?.pageItems.length}
          total={applications?.totalItems || 0}
          pageSizeLabel={PAGINATION_DROPDOWN_LABEL}
        />
      </footer>
    </div>
  )
}
